/**
 * PrioritizationMemoBody — the memo, as read on screen and as printed.
 *
 * One component for both, so the page and the PDF can never say different
 * things. Content comes from report_artifacts.content (kind
 * 'prioritization_memo', assembled by src/lib/prioritizationMemo.ts).
 *
 * Recorded words (notes, override reasons, transcript passages) are shown as
 * quotations with who and when. The model's prose is marked "AI" every time it
 * appears. Brief K slice 3; roadmap AI-5.
 */
import { formatDate as formatSharedDate } from '@/i18n/formatters';
import type { MemoItem, MemoReason, PrioritizationMemoContent } from '../../../lib/prioritizationMemo';

const DATE_STYLE: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };

/** The shared formatter, with "nothing recorded" as null rather than an empty string. */
const dateOrNull = (iso: string | null): string | null => (iso ? formatSharedDate(iso, DATE_STYLE) || null : null);

function formatScore(score: number | null): string | null {
  if (score == null) return null;
  return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

function AiMark() {
  return (
    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-[var(--text-sec)] border border-[var(--rule)] align-middle">
      AI
    </span>
  );
}

function Reason({ reason }: { reason: MemoReason }) {
  const date = dateOrNull(reason.at);
  const attribution =
    reason.kind === 'from_the_room'
      ? `From the discussion${reason.timestamp ? ` at ${reason.timestamp}` : ''}${reason.who ? ` · confirmed by ${reason.who}` : ''}`
      : reason.kind === 'method_note'
        ? 'Noted by the method'
        : [reason.who ?? 'Someone in the session', date].filter(Boolean).join(', ');
  return (
    <blockquote className="border-l-2 border-[var(--rule)] pl-3 my-2" data-testid="memo-recorded">
      <p className="text-sm text-[var(--text-pri)] leading-relaxed">“{reason.text}”</p>
      <footer className="mt-0.5 text-xs text-[var(--text-ter)]">— {attribution}</footer>
    </blockquote>
  );
}

function Item({ item, belowLine }: { item: MemoItem; belowLine?: boolean }) {
  const score = formatScore(item.score);
  return (
    <li className="py-4 border-b border-[var(--rule)] last:border-0 break-inside-avoid" data-testid="memo-item">
      <div className="flex items-baseline gap-3">
        <span className="font-mono text-sm text-[var(--text-ter)] w-8 shrink-0">#{item.rank}</span>
        <div className="min-w-0 flex-1">
          <p className={`text-base font-medium ${belowLine ? 'text-[var(--text-sec)]' : 'text-[var(--text-pri)]'}`}>{item.title}</p>
          {(score || item.tiedWithRanks.length > 0) && (
            <p className="text-xs text-[var(--text-ter)] mt-0.5">
              {score ? `Score ${score}` : null}
              {score && item.tiedWithRanks.length > 0 ? ' · ' : null}
              {item.tiedWithRanks.length > 0
                ? `same score as #${item.tiedWithRanks.join(', #')}`
                : null}
            </p>
          )}
          {item.reasons.map((r, i) => (
            <Reason key={i} reason={r} />
          ))}
          {item.reasons.length === 0 && (
            <p className="mt-2 text-sm text-[var(--text-ter)] italic">Nothing was recorded about this item.</p>
          )}
          {item.explained && (
            <p className="mt-2 text-sm text-[var(--text-sec)] leading-relaxed">
              {item.explained}
              <AiMark />
            </p>
          )}
        </div>
      </div>
    </li>
  );
}

export interface PrioritizationMemoBodyProps {
  memo: PrioritizationMemoContent;
  generatedAt: Date | null;
}

export function PrioritizationMemoBody({ memo, generatedAt }: PrioritizationMemoBodyProps) {
  const published = dateOrNull(memo.session.publishedAt);
  const written = generatedAt ? dateOrNull(generatedAt.toISOString()) : null;
  const where = [memo.session.cycleName, memo.session.trackName].filter(Boolean).join(' · ');
  const meta = [
    memo.session.name,
    where || null,
    memo.session.toolName,
    memo.session.participantCount ? `${memo.session.participantCount} participant${memo.session.participantCount === 1 ? '' : 's'}` : null,
    published ? `published ${published}` : null,
  ].filter(Boolean);

  return (
    <article className="space-y-8 text-[var(--text-pri)]" data-testid="prioritization-memo">
      <header className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-[0.14em] text-[var(--text-ter)]">Prioritization memo</p>
        <h1 className="text-2xl font-semibold leading-tight">
          {memo.headline}
          {memo.sources.aiWritten && <AiMark />}
        </h1>
        <p className="text-sm text-[var(--text-sec)]">{meta.join(' · ')}</p>
        {memo.criteria.length > 0 && (
          <p className="text-sm text-[var(--text-sec)]">
            Criteria: {memo.criteria.map((c) => (c.weight != null ? `${c.label} (${c.weight})` : c.label)).join(', ')}
          </p>
        )}
      </header>

      {memo.summary ? (
        <section>
          <p className="text-base leading-relaxed">
            {memo.summary}
            <AiMark />
          </p>
        </section>
      ) : memo.sources.aiNote ? (
        <p className="text-sm text-[var(--text-ter)] italic">{memo.sources.aiNote}</p>
      ) : null}

      <section>
        <h2 className="text-lg font-semibold">
          {memo.cutLine != null && memo.cutLine > 0 ? `Above the line (${memo.aboveLine.length})` : `The top ${memo.aboveLine.length}`}
        </h2>
        <ol className="mt-2">
          {memo.aboveLine.map((item) => (
            <Item key={item.itemId} item={item} />
          ))}
        </ol>
        {memo.notCoveredCount > 0 && (
          <p className="text-sm text-[var(--text-ter)]">
            No cut line was set, so the memo covers the top {memo.aboveLine.length}. {memo.notCoveredCount} more item
            {memo.notCoveredCount === 1 ? ' is' : 's are'} in the ranking.
          </p>
        )}
      </section>

      {memo.nearMisses.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold">Just below the line</h2>
          <ol className="mt-2">
            {memo.nearMisses.map((item) => (
              <Item key={item.itemId} item={item} belowLine />
            ))}
          </ol>
          {memo.belowLineCount > 0 && (
            <p className="text-sm text-[var(--text-ter)]">
              {memo.belowLineCount} more item{memo.belowLineCount === 1 ? '' : 's'} below the line.
            </p>
          )}
        </section>
      )}

      {memo.tradeOffs && (
        <section>
          <h2 className="text-lg font-semibold">What was traded off</h2>
          <p className="mt-2 text-base leading-relaxed">
            {memo.tradeOffs}
            <AiMark />
          </p>
        </section>
      )}

      {memo.overrides.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold">Ranks changed by hand</h2>
          <ul className="mt-2 space-y-3">
            {memo.overrides.map((o, i) => (
              <li key={i} className="break-inside-avoid">
                <p className="text-sm font-medium">
                  {o.title}
                  {o.fromRank != null || o.toRank != null
                    ? ` — ${o.fromRank != null ? `from #${o.fromRank}` : ''}${o.fromRank != null && o.toRank != null ? ' ' : ''}${o.toRank != null ? `to #${o.toRank}` : ''}`
                    : ''}
                </p>
                <blockquote className="border-l-2 border-[var(--rule)] pl-3 mt-1">
                  <p className="text-sm leading-relaxed">“{o.reason}”</p>
                  <footer className="mt-0.5 text-xs text-[var(--text-ter)]">
                    — {[o.by ?? 'The facilitator', dateOrNull(o.at)].filter(Boolean).join(', ')}
                  </footer>
                </blockquote>
              </li>
            ))}
          </ul>
        </section>
      )}

      <footer className="pt-4 border-t border-[var(--rule)] space-y-2 text-xs text-[var(--text-ter)] leading-relaxed">
        {memo.sources.documents.length > 0 && (
          <p>
            Documents on the session:{' '}
            {memo.sources.documents.map((d) => d.title).join('; ')}.
          </p>
        )}
        <p>
          {memo.sources.recordedReasonCount > 0
            ? `${memo.sources.recordedReasonCount} recorded reason${memo.sources.recordedReasonCount === 1 ? '' : 's'} quoted.`
            : 'Nobody recorded a reason for these items in the session.'}{' '}
          {memo.sources.transcriptChars > 0
            ? 'The AI read the session transcript; it is not quoted unless someone linked a passage to an item.'
            : 'No transcript was attached to the session.'}
        </p>
        <p>
          Quotations are copied from the session record. Text marked AI was written by a model and is advisory: ranking informs
          priority, it does not establish it.
          {written ? ` Written ${written}.` : ''}
        </p>
      </footer>
    </article>
  );
}

export default PrioritizationMemoBody;
